import CheckCircle2Icon from '@lucide/svelte/icons/check-circle-2';
import { authLoading, isSignedIn } from '@repo/sharedlib/src/stores/auth.store';
import { dataLoadState } from '@repo/sharedlib/src/stores/cloud.sync.store';
import type { Component } from 'svelte';
import toast from 'svelte-5-french-toast';
import { toast as sonnerToast } from 'svelte-sonner';
import { get, type Readable } from 'svelte/store';
import InfoIcon from '../components/ui/InfoIcon.svelte';
import Spinner from '../components/ui/spinner/spinner.svelte';
import SyncLoadingToast from '../components/ui/SyncLoadingToast.svelte';
import TourAvatarIcon from '../components/ui/TourAvatarIcon.svelte';

// Note: We previously patched sonner's internal toast state to prevent undefined height entries.
// This has been removed because the internal module 'svelte-sonner/dist/toast-state.svelte.js'
// is not exported in the package's package.json and causes build failures on Windows.

let lastTourToastAt: number | null = null;
let activeTourToastId: string | number | null = null;

export const showErrorToast = (message: string) => {
	toast.error(message, { style: 'z-index: 60' });
};

export const showErrorLogToast = (message: string) => {
	sonnerToast.error(message, {
		duration: 10000,
		class: 'error-log-toast'
	});
};

export const showUpdateAvailableToast = (
	version: string,
	onUpdate?: () => void,
	onDismiss?: () => void
) => {
	sonnerToast('Update available', {
		description: `Version ${version} is ready to install`,
		duration: Infinity, // Toast stays until dismissed
		closeButton: true, // Show X button
		action: {
			label: 'Update',
			onClick: () => {
				if (onUpdate) onUpdate();
			}
		},
		onDismiss: () => {
			// Only call onDismiss if toast was dismissed (not if action was clicked)
			if (onDismiss) {
				onDismiss();
			}
		},
		icon: InfoIcon,
		classes: {
			title: '!font-semibold',
			icon: 'arrow_down_square',
			toast: '!border-[2px] !border-blue-500 dark:!border-gray-700',
			actionButton:
				'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600'
		}
	} as any); // Type assertion needed due to svelte-sonner limitations
};

export const showTourAvailableToast = (
	onTakeTour?: () => void,
	onDismiss?: () => void,
	title?: string,
	description?: string,
	icon?: Component<any>
) => {
	const TOUR_TOAST_ID = 'tour-available';
	const TOUR_TOAST_MIN_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
	const now = Date.now();

	// Prevent showing the tour toast too frequently
	if (
		(lastTourToastAt && now - lastTourToastAt < TOUR_TOAST_MIN_INTERVAL_MS) ||
		activeTourToastId
	) {
		return;
	}

	sonnerToast(title || 'Take a quick tour', {
		id: TOUR_TOAST_ID,
		description: description || 'Learn how to use Retain to draft emails from your knowledge base',
		duration: Infinity, // Toast stays until dismissed
		closeButton: true, // Show X button
		action: {
			label: 'Start Tour',
			onClick: () => {
				if (onTakeTour) onTakeTour();
			}
		},
		onDismiss: () => {
			activeTourToastId = null;
			if (onDismiss) {
				onDismiss();
			}
		},
		icon: icon || TourAvatarIcon,
		classes: {
			title: '!font-semibold',
			icon: 'tour-avatar',
			toast: '!border-[2px] !border-blue-500 dark:!border-gray-700 !w-[420px]',
			actionButton:
				'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600'
		}
	} as any); // Type assertion needed due to svelte-sonner limitations

	activeTourToastId = TOUR_TOAST_ID;
	lastTourToastAt = now;
};

export const showSuccessToast = (message: string) => {
	toast.success(message, { style: 'z-index: 60' });
};

type ToastContent = string | Component<any> | { component: Component<any>; props?: any } | null;

export const showPromiseToast = <T>(
	promise: Promise<T>,
	{
		loading,
		success,
		error,
		loadingMessageStore,
		waitForDataLoadState
	}: {
		loading: string;
		success: string | ((data: T) => ToastContent);
		error: string;
		loadingMessageStore?: Readable<{ currentOperation?: string; isRunning?: boolean }>;
		waitForDataLoadState?: boolean;
	}
) => {
	const shouldWaitForDataLoadState = waitForDataLoadState ?? Boolean(loadingMessageStore);
	// Use custom component if loadingMessageStore is provided (for reactive updates)
	// Otherwise use string loading message
	const loadingContent = loadingMessageStore ? SyncLoadingToast : loading;

	// Show loading toast with custom component or string
	const id =
		typeof loadingContent === 'string'
			? sonnerToast.loading(loadingContent, {
					icon: Spinner,
					duration: Infinity
				})
			: sonnerToast(
					loadingContent as any,
					{
						duration: Infinity
						// Don't specify type - the custom component handles its own spinner
					} as any
				);

	// Track promise resolution
	let promiseResolved = false;
	let promiseResult: T | null = null;
	let promiseError: unknown = null;

	const showFinalToast = () => {
		// Clean up subscriptions
		if (unsubscribe) {
			unsubscribe();
			unsubscribe = null;
		}
		if (unsubscribeAuth) {
			unsubscribeAuth();
			unsubscribeAuth = null;
		}
		if (unsubscribeAuthLoading) {
			unsubscribeAuthLoading();
			unsubscribeAuthLoading = null;
		}
		if (unsubscribeDataLoad) {
			unsubscribeDataLoad();
			unsubscribeDataLoad = null;
		}

		if (promiseError) {
			sonnerToast.error(error, {
				id,
				duration: 4000
			});
		} else if (promiseResult !== null) {
			const successContent = typeof success === 'function' ? success(promiseResult) : success;

			if (successContent === null) {
				sonnerToast.dismiss(id);
				return;
			}

			if (typeof successContent === 'string') {
				// Use 2 second duration for "Welcome back!", 4 seconds for other messages
				const duration = successContent === 'Welcome back!' ? 2000 : 4000;
				// Update to success toast with checkmark icon
				sonnerToast.success(successContent, {
					id,
					icon: CheckCircle2Icon,
					duration
				});
			} else {
				// For custom components (with or without props)
				const component =
					typeof successContent === 'object' && 'component' in successContent
						? successContent.component
						: (successContent as Component<any>);
				const props =
					typeof successContent === 'object' && 'component' in successContent
						? successContent.props || {}
						: {};

				// Dismiss the loading toast and create a fresh unstyled toast for custom component
				sonnerToast.dismiss(id);
				sonnerToast(
					component as any,
					{
						duration: 4000,
						unstyled: true,
						...props
					} as any
				);
			}
		}
	};

	// No need to subscribe to loadingMessageStore - the custom component handles reactivity
	let unsubscribe: (() => void) | null = null;

	// Subscribe to auth state to dismiss toast when user is not signed in and auth is not loading
	let unsubscribeAuth: (() => void) | null = null;
	let unsubscribeAuthLoading: (() => void) | null = null;
	if (loadingMessageStore) {
		const checkAndDismiss = () => {
			const $isSignedInValue = get(isSignedIn);
			const $authLoadingValue = get(authLoading);
			// Dismiss toast if user is not signed in and auth is not loading
			if (!$isSignedInValue && !$authLoadingValue) {
				sonnerToast.dismiss(id);
				if (unsubscribeAuth) {
					unsubscribeAuth();
					unsubscribeAuth = null;
				}
				if (unsubscribeAuthLoading) {
					unsubscribeAuthLoading();
					unsubscribeAuthLoading = null;
				}
			}
		};
		unsubscribeAuth = isSignedIn.subscribe(checkAndDismiss);
		unsubscribeAuthLoading = authLoading.subscribe(checkAndDismiss);
	}

	// Subscribe to dataLoadState to know when everything is complete
	let unsubscribeDataLoad: (() => void) | null = null;
	if (shouldWaitForDataLoadState) {
		unsubscribeDataLoad = dataLoadState.subscribe((state) => {
			// When data load is complete and promise has resolved, update toast to show final state
			if (state === 'complete' && promiseResolved) {
				showFinalToast();
			}
		});
	}

	promise
		.then((p) => {
			promiseResolved = true;
			promiseResult = p;

			if (!shouldWaitForDataLoadState) {
				showFinalToast();
				return p;
			}

			// Check current data load state - if already complete, show final toast immediately
			const currentState = get(dataLoadState);
			if (currentState === 'complete') {
				showFinalToast();
			}
			// Otherwise, wait for dataLoadState to become 'complete' (handled in subscription above)
			return p;
		})
		.catch((e) => {
			promiseResolved = true;
			promiseError = e;

			// On error, show error toast immediately
			showFinalToast();
		});

	return promise;
};

export { Toaster } from 'svelte-5-french-toast';
export { Toaster as SonnerToaster } from 'svelte-sonner';
export { sonnerToast, toast };
